import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Container, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

const StickyScroll = ({ content }: { content: any[] }) => {
  const [activeCard, setActiveCard] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>(Array(content.length).fill(null));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Simplified scroll detection for better mobile performance
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;
    let isAutoScrolling = false;

    const handleScroll = () => {
      if (isAutoScrolling) return;
      
      // Clear any existing timeout for better performance
      clearTimeout(scrollTimeout);
      
      // Use requestAnimationFrame for smoother scrolling detection
      requestAnimationFrame(() => {
        updateActiveCard();
      });
      
      // Set a timeout to check if scrolling has stopped
      scrollTimeout = setTimeout(() => {
        if (!isAutoScrolling) {
          checkScrollPosition();
        }
      }, 100); // Reduced timeout for better responsiveness
    };

    const updateActiveCard = () => {
      if (!container || isAutoScrolling) return;
      
      const containerRect = container.getBoundingClientRect();
      let bestIntersection = 0;
      let bestIndex = activeCard;
      
      // Find which card has the most visible area in the viewport
      cardRefs.current.forEach((card, index) => {
        if (!card) return;
        
        const rect = card.getBoundingClientRect();
        const visibleTop = Math.max(rect.top, containerRect.top);
        const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
        
        if (visibleBottom > visibleTop) {
          const intersection = (visibleBottom - visibleTop) / rect.height;
          if (intersection > bestIntersection) {
            bestIntersection = intersection;
            bestIndex = index;
          }
        }
      });
      
      if (bestIndex !== activeCard) {
        setActiveCard(bestIndex);
      }
    };

    const checkScrollPosition = () => {
      if (isAutoScrolling || !isMobile) return;
      
      const currentIndex = activeCard;
      const currentCardEl = cardRefs.current[currentIndex];
      
      if (currentCardEl) {
        const rect = currentCardEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Auto-align card if it's more than 70% visible but not fully aligned
        const visiblePercentage = (Math.min(rect.bottom, containerRect.bottom) - 
                                  Math.max(rect.top, containerRect.top)) / rect.height;
        
        const isNearlyVisible = visiblePercentage > 0.7;
        const isNotAligned = Math.abs(rect.top - containerRect.top) > 20;
        
        if (isNearlyVisible && isNotAligned) {
          isAutoScrolling = true;
          currentCardEl.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
          
          setTimeout(() => {
            isAutoScrolling = false;
          }, 500);
        }
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    // Handle initial active card
    updateActiveCard();
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [activeCard, content.length, isMobile]);

  // Manual navigation for mobile
  const handleCardNavigation = (index: number) => {
    if (index >= 0 && index < content.length) {
      const targetCard = cardRefs.current[index];
      if (targetCard && containerRef.current) {
        targetCard.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        setActiveCard(index);
      }
    }
  };

  // Card and image transition variants
  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const imageVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.3 }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: { xs: 'auto', md: '100vh' },
        minHeight: { xs: '100vh', md: '600px' },
        overflow: 'hidden',
        backgroundColor: '#f3f4f6',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      {/* Mobile Navigation Indicators */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 5,
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          {content.map((_, index) => (
            <Box
              key={index}
              onClick={() => handleCardNavigation(index)}
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: activeCard === index ? '#172554' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: activeCard === index ? 'scale(1.2)' : 'scale(1)',
              }}
            />
          ))}
        </Box>
      )}

      {/* Mobile Image Section (appears before content on mobile) */}
      {isMobile && (
        <Box
          component={motion.div}
          layout
          sx={{
            height: '35vh',
            minHeight: '200px',
            maxHeight: '300px',
            width: '100%',
            backgroundColor: "#f8fafc",
            backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(216, 241, 230, 0.46) 0%, rgba(233, 226, 248, 0.46) 90.2%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
            borderRadius: '12px 12px 0 0',
            overflow: 'hidden',
            boxShadow: 'inset 0 0 20px rgba(0,0,0,0.03)',
          }}
        >
          <Box 
            sx={{ 
              width: '90%', 
              height: '90%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <motion.div
              key={`mobile-image-${activeCard}`}
              variants={imageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ width: '100%', height: '100%' }}
            >
              {content[activeCard].content}
            </motion.div>
          </Box>
        </Box>
      )}

      {/* Left Scrollable Section */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: { xs: 2, md: 4 },
          height: { xs: isMobile ? 'calc(65vh - 40px)' : '55vh', md: '100vh' },
          minHeight: { xs: '300px', md: '600px' },
          maxHeight: { xs: isMobile ? '100%' : '55vh', md: '100vh' },
          '&::-webkit-scrollbar': { 
            width: '4px',
            display: { xs: 'none', md: 'block' },
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555',
            },
          },
          scrollBehavior: 'smooth',
          scrollbarWidth: { xs: 'none', md: 'thin' },
          scrollSnapType: { xs: 'y mandatory', md: 'none' },
          transition: 'all 0.3s ease',
        }}
      >
        {content.map((item, index) => (
          <motion.div
            key={index}
            ref={el => cardRefs.current[index] = el}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={cardVariants}
            style={{
              scrollSnapAlign: isMobile ? 'start' : 'none',
            }}
          >
            <Box
              sx={{
                height: { xs: 'auto', md: '100vh' },
                minHeight: { xs: '300px', md: '70vh' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: { xs: 2, md: 4 },
                paddingTop: { xs: 2, md: 4 },
                paddingBottom: { xs: 4, md: 4 },
                textAlign: { xs: 'left', md: 'left' },
                marginBottom: { xs: 2, md: 0 },
                position: 'relative',
                '&::after': index !== content.length - 1 ? {
                  content: '""',
                  position: 'absolute',
                  bottom: '0px',
                  left: '5%',
                  width: '90%',
                  height: '1px',
                  background: 'linear-gradient(to right, transparent, rgba(23, 37, 84, 0.15), transparent)',
                  display: { xs: 'block', md: 'none' },
                } : {},
                transition: 'all 0.3s ease-in-out',
                transform: activeCard === index ? 'scale(1.01)' : 'scale(1)',
                opacity: activeCard === index ? 1 : { xs: 0.85, md: 0.7 },
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.3rem', sm: '1.5rem', md: '2rem' },
                  fontWeight: 600,
                  color: '#172554',
                  marginBottom: 1.5,
                  lineHeight: 1.3,
                  background: activeCard === index ? 
                    'linear-gradient(90deg, #172554 0%, #1e40af 100%)' : 
                    'none',
                  WebkitBackgroundClip: activeCard === index ? 'text' : 'none',
                  WebkitTextFillColor: activeCard === index ? 'transparent' : 'inherit',
                  transition: 'all 0.5s ease',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                  color: '#172554',
                  opacity: 0.9,
                  lineHeight: 1.5,
                  marginBottom: 2,
                }}
              >
                {item.description}
              </Typography>
              {/* Bullet Points - Optimized for mobile */}
              <Box
                component="ul"
                sx={{
                  mt: 1,
                  pl: { xs: 2.5, md: 3 },
                  textAlign: 'left',
                  listStyleType: 'disc',
                  '& li': {
                    fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.05rem' },
                    marginBottom: '8px',
                    paddingBottom: '2px',
                    display: 'list-item',
                    whiteSpace: 'normal',
                    color: '#172554',
                    opacity: 0.8,
                    lineHeight: 1.4,
                    position: 'relative',
                    paddingLeft: '5px',
                    maxWidth: '100%',
                    '&::marker': {
                      color: '#172554',
                    },
                  },
                }}
              >
                {item.points.map((point: string, idx: number) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0,
                      transition: { 
                        delay: 0.05 * idx,
                        duration: 0.4
                      } 
                    }}
                  >
                    {point}
                  </motion.li>
                ))}
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* Right Sticky Section - Only for tablet and desktop */}
      {!isMobile && (
        <Box
          component={motion.div}
          layout
          sx={{
            flex: { xs: 'none', md: 1 },
            position: { xs: 'relative', md: 'sticky' },
            top: 0,
            height: { xs: '45vh', md: '100vh' },
            backgroundColor: "#f3f4f6",
            backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(216, 241, 230, 0.46) 0%, rgba(233, 226, 248, 0.46) 90.2%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
            borderRadius: { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
            overflow: 'hidden',
            boxShadow: 'inset 0 0 20px rgba(0,0,0,0.03)',
          }}
        >
          <Box 
            sx={{ 
              width: '90%', 
              height: '90%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <motion.div
              key={`desktop-image-${activeCard}`}
              variants={imageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ width: '100%', height: '100%' }}
            >
              {content[activeCard].content}
            </motion.div>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export function DesseratationProposals() {
  const content = [
    {
      title: "Understanding the Dissertation Proposal",
      description:
        "A dissertation proposal is a crucial document that outlines your research objectives, methodology, and expected outcomes. It acts as a roadmap for your research journey, ensuring clarity and direction.",
      points: [
        "Clearly defines the topic and research problem.",
        "Summarizes existing research and identifies gaps.",
        "Details the research methods and data collection approach.",
        "Highlights anticipated findings and their significance.",
      ],
      content: (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src="173156179008out.jpg"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              transition: 'transform 0.5s ease',
            }}
            alt="diverse team of academic experts"
          />
        </Box>
      ),
    },
    {
      title: "Importance of a Well-Written Proposal",
      description:
        "A strong proposal increases the chances of approval from supervisors and funding committees. It sets the stage for a successful dissertation by demonstrating feasibility and academic contribution.",
      points: [
        "Ensures acceptance from academic and funding bodies.",
        "Provides a clear research direction.",
        "Confirms that the study is practical and achievable.",
        "Demonstrates thorough planning and scholarly depth.",
      ],
      content: (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src="team2.jpg"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              transition: 'transform 0.5s ease',
            }}
            alt="expert researchers collaborating"
          />
        </Box>
      ),
    },
    {
      title: "Our Approach to Proposal Writing",
      description:
        "We follow a strategic approach to ensure your proposal meets academic standards and aligns with research objectives.",
      points: [
        "Helping you choose a relevant and research-worthy topic.",
        "Identifying key studies and research gaps.",
        "Defining research methods and data collection strategies.",
        "Ensuring compliance with university guidelines.",
      ],
      content: (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src="team4.jpg"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              transition: 'transform 0.5s ease',
            }}
            alt="research analysis in progress"
          />
        </Box>
      ),
    },
    {
      title: "Why Choose Our Proposal Writing Assistance?",
      description:
        "Our expertise ensures that your proposal is meticulously crafted, well-structured, and impactful.",
      points: [
        "Professional academic writers with research experience.",
        "Tailored solutions based on your research needs.",
        "100% original and well-researched proposals.",
        "Meeting deadlines without compromising quality.",
      ],
      content: (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src="team5.jpg"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              transition: 'transform 0.5s ease',
            }}
            alt="quality assurance process"
          />
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
      </motion.div>
      <Typography 
          variant="h5" 
          fontWeight={600} 
          gutterBottom 
          textAlign={"center"}
          color={"#172554"}
          sx={{ fontSize: { xs: "20px", sm: "24px", md: "28px" } }}
        >      Proposal{" "}
           <span
             style={{
               background:
                 "linear-gradient(-96deg, rgb(255 255 255 / 0%) 0%, rgb(255 249 220 / 0%) 28%, rgb(253, 103, 16) 100%, rgb(255 255 255 / 0%) 100%)",
               padding: "0px 5px",
             }}
           >
             Writing Assistance
           </span>{" "}
      </Typography>
      <StickyScroll content={content} />
    </Container>
  );
}

export default DesseratationProposals;